import React from 'react';
import moment from 'moment';
import CustomButton from '../../../../components/common/CustomButton';
import Portal from '../../../../components/Portal';
import { HiX } from 'react-icons/hi';

interface Task {
  id: string;
  item_id: number;
  task_title: string;
  project_name: string;
  project_color: string;
  duration: number;
  completion?: number;
  start: string;
  end: string;
  project_id?: string;
}

interface AssignTaskModalProps {
  showViewMoreOpenSlot: boolean;
  curSelectAssigneeGroup: any;
  additionalTask: Task[];
  unassignedTasksLoading: boolean;
  onTaskRadioButton: (e: any, task: any) => void;
  onSaveClick: () => void;
  onCancelClick: () => void;
  onCloseModal: () => void;
}

const AssignTaskModal: React.FC<AssignTaskModalProps> = ({
  showViewMoreOpenSlot,
  curSelectAssigneeGroup,
  additionalTask,
  unassignedTasksLoading,
  onTaskRadioButton,
  onSaveClick,
  onCancelClick,
  onCloseModal
}) => {
  if (!showViewMoreOpenSlot) return null;

  return (
    <Portal>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6">
        <button
          type="button"
          aria-label="Close assign task modal"
          className="absolute inset-0 w-full h-full bg-black/50 backdrop-blur-sm cursor-default z-10"
          onClick={onCloseModal}
        />
        <dialog
          open
          className="z-20 bg-secondary-light dark:bg-secondary-dark rounded-lg w-full max-w-xl mx-auto self-center shadow-2xl transform transition-all max-h-[85vh] flex flex-col"
          aria-labelledby="assign-task-title"
        >
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex justify-between items-center">
              <h3 id="assign-task-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                Assign Task to Open Slot
              </h3>
              <button
                type="button"
                onClick={onCloseModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <span className="sr-only">Close</span>
                <HiX className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div>
              <span className="text-gray-700 dark:text-gray-300 text-base font-normal leading-normal">Open tasks that </span>
              <span className="text-gray-900 dark:text-white text-base font-semibold leading-normal">{curSelectAssigneeGroup?.title}</span>
              <span className="text-gray-700 dark:text-gray-300 text-base font-normal leading-normal"> with their expertise could be assigned to in this time slot, organized by Cadence's recommendation:</span>
            </div>
            <div className="mt-4 space-y-3">
              {additionalTask.map((task: Task) => {
                return (
                  <label className="justify-start items-center gap-2 flex cursor-pointer" key={task.item_id} htmlFor={task.id}>
                    <input
                      id={task.id}
                      type="radio"
                      name="additionalTask"
                      className="mr-4 mt-[2px] cursor-pointer accent-primary-dark focus:ring-primary-dark"
                      onChange={(e) => onTaskRadioButton(e, task)}
                    />
                    <div className="flex-col justify-start items-start gap-0.5 inline-flex">
                      <div>
                        <span className="text-gray-900 dark:text-white text-sm font-bold leading-[17.50px]" title={task?.project_name}>{task?.project_name}</span>
                        <span className="text-gray-900 dark:text-white text-sm font-medium leading-[17.50px]" title={task?.task_title}> : {task?.task_title}</span>
                      </div>
                      <div className="text-gray-500 dark:text-gray-400 text-xs font-normal leading-[18px]">{task?.duration} days - {moment(task?.start).format("MMM DD YYYY")} - {moment(task?.end).format("MMM DD YYYY")}</div>
                    </div>
                  </label>
                )
              })}
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex space-x-3 justify-end items-center">
              <CustomButton
                type="button"
                variant="secondary"
                onClick={onCancelClick}
              >
                Cancel
              </CustomButton>

              <CustomButton
                onClick={onSaveClick}
                isLoading={unassignedTasksLoading}
              >
                <span>Assign</span>
              </CustomButton>
            </div>
          </div>
        </dialog>
      </div>
    </Portal>
  );
};

export default AssignTaskModal; 